import merge from 'lodash/merge';
import qs from 'query-string';
import { isAppModule } from '@utils/constants';
import instance from './axiosInstance';
import type { AxiosPromise, AxiosRequestConfig } from 'axios';

const baseURLV2 = `${BUILD_VARS.QUERY_DOMAIN}/v2.0`;

export type FilterBulkDeleteResponseWithError = {
  success: number[];
  failed: number[];
  errors: Record<number, string>;
};

// TODO: after filter management GA then the checking for it can be removed
export const getV2AxiosConfig = (config?: AxiosRequestConfig) => {
  const baseConfig = config || {};

  return {
    baseURL: baseURLV2,
    ...baseConfig,
  };
};

export function getMeta<T = any>(config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.get<T>('/filters/meta', reqConfig).then((resp) => resp.data);
}

export function getAutoCompletion<T = any>(
  url: string,
  pattern: string | number,
  config: AxiosRequestConfig = {},
) {
  const configWithPattern = merge(config, { params: { pattern } });
  return instance.get<T>(url, configWithPattern);
}

export function getFilters<T = any>(config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.get<T>('/filters', reqConfig).then((resp) => resp.data);
}

export function getFiltersBySearch<T = any>(config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.get<T>('/filters/search', reqConfig).then((resp) => resp.data);
}

export function createFilter<T = any>(data, config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.post<T>('/filters', data, reqConfig);
}

export function updateFilter<T = any>(fid, data, config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  if (reqConfig.baseURL) {
    return instance.put<T>(`/filter/${fid}`, data, reqConfig);
  }
  return instance.post<T>(`/filter/${fid}`, data, reqConfig);
}

export function deleteFilter(id: number, config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.delete(`/filter/${id}`, reqConfig) as AxiosPromise<void>;
}

export function deleteFilters(ids: number[], config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.delete(`/filters`, {
    ...reqConfig,
    data: { ids },
  }) as AxiosPromise<FilterBulkDeleteResponseWithError | void>;
}

export function enableFilter<T = any>(fid, config?: AxiosRequestConfig) {
  const reqConfig = getV2AxiosConfig(config);
  return instance.put<T>(`/filter/enable/${fid}`, {}, reqConfig);
}

// TODO: NOTE: backend API does not process rules
// for now, this is only used to load default "All Traffic"
export function searchFilter(query, config?: AxiosRequestConfig) {
  return instance.post('/filters/search', query, config);
}

export function getFiltersByIds<T = any>(ids: number[]) {
  const config = {
    params: { ids },
    paramsSerializer: (params: Record<string, any>) =>
      qs.stringify(params, { arrayFormat: 'comma' }),
  };

  if (isAppModule) {
    return instance.get<T>(`/appData/filters`, config).then((resp) => resp.data);
  }

  return instance.get<T>(`/filters`, { baseURL: baseURLV2, ...config }).then((resp) => resp.data);
}

// TODO: after app filter api is migrated into new filter service then we can keep the app/video filter api path consistent.
// app experience filter methods
export function getAppMeta<T = any>(config?: AxiosRequestConfig) {
  return instance.get<T>('/filters/meta', config).then((resp) => resp.data);
}

export function createFilterForAppExperience<T = any>(data, config?: AxiosRequestConfig) {
  return instance.post<T>('/appData/filter', data, config);
}

export function updateFilterForAppExperience<T = any>(fid, data, config?: AxiosRequestConfig) {
  return instance.put<T>(`/appData/filter/${fid}`, data, config);
}

export function getFiltersForAppExperience<T = any>(config?: AxiosRequestConfig) {
  return instance.get<T>('/appData/filters', config).then((resp) => resp.data);
}

export function deleteFilterForAppExperience(id: number, config?: AxiosRequestConfig) {
  return instance.delete(`/appData/filter/${id}`, config) as AxiosPromise<void>;
}

export function deleteFiltersForAppExperience(ids: number[], config?: AxiosRequestConfig) {
  return instance.delete(`/appData/filters`, {
    ...config,
    data: { ids },
  }) as AxiosPromise<FilterBulkDeleteResponseWithError | void>;
}

export function getCustomTagAutoCompletion<T = any>(config?: AxiosRequestConfig) {
  return instance.get<T>('filters/meta/tag_keys', config).then((resp) => resp.data);
}
